import { useCallback, useState } from 'react';
import type { AccountTypeToggleGroupValue } from 'ui-components';

import type { ProductInfo } from './productListsHelpers';

export type ModalView = 'closed' | 'save' | 'create';

export type SaveToListModalState = {
  view: ModalView;
  product: ProductInfo | null;
  initialSelectedListIds: string[];
  fromCreateFlow: boolean;
  createdListName: string | null;
  createAccountType: AccountTypeToggleGroupValue;
  isSubmitting: boolean;
  serverError: boolean;
  openToken: number;
};

const INITIAL_STATE: SaveToListModalState = {
  view: 'closed',
  product: null,
  initialSelectedListIds: [],
  fromCreateFlow: false,
  createdListName: null,
  createAccountType: 'personal',
  isSubmitting: false,
  serverError: false,
  openToken: 0,
};

export function useSaveToListModal() {
  const [state, setState] = useState<SaveToListModalState>(INITIAL_STATE);

  const openSave = useCallback(
    (product: ProductInfo, initialSelectedListIds: string[]) => {
      setState((prev) => ({
        ...INITIAL_STATE,
        view: 'save',
        product,
        initialSelectedListIds,
        openToken: prev.openToken + 1,
      }));
    },
    []
  );

  const openCreate = useCallback((accountType: AccountTypeToggleGroupValue) => {
    setState((prev) => ({
      ...prev,
      view: 'create',
      createAccountType: accountType,
      serverError: false,
    }));
  }, []);

  const returnToSaveAfterCreate = useCallback(
    (initialSelectedListIds: string[], createdListName: string) => {
      setState((prev) => ({
        ...prev,
        view: 'save',
        fromCreateFlow: true,
        createdListName,
        initialSelectedListIds,
        isSubmitting: false,
        serverError: false,
        openToken: prev.openToken + 1,
      }));
    },
    []
  );

  const close = useCallback(() => setState(INITIAL_STATE), []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setState((prev) => ({ ...prev, isSubmitting }));
  }, []);

  const setServerError = useCallback((serverError: boolean) => {
    setState((prev) => ({ ...prev, serverError }));
  }, []);

  return {
    ...state,
    openSave,
    openCreate,
    returnToSaveAfterCreate,
    close,
    setSubmitting,
    setServerError,
  };
}
